import React, { useState, useRef } from 'react';
import "./Askfameo.scss";
import { GoLightBulb } from "react-icons/go";
import { HiOutlineHashtag } from "react-icons/hi";
import { PiMagicWandFill } from "react-icons/pi";
import { IoDiamondOutline } from "react-icons/io5";
import { FaRegCheckCircle } from "react-icons/fa";
import { CiShare2 } from "react-icons/ci";
import { IoReloadOutline } from "react-icons/io5";
import { CiCirclePlus } from "react-icons/ci";
import { MdOutlineMicNone, MdClose } from "react-icons/md";
import { IoMdSend } from "react-icons/io";
import { CiSearch } from "react-icons/ci";
import { IoArrowBack } from "react-icons/io5";
import { FaCopy, FaCheck } from "react-icons/fa6";
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAiQuota } from '../../components/Hooks/useAiQuota';
import { compressImageForAi } from '../../utils/compressImageForAi';
import axios from '../../components/Hooks/axios';

const AskFameo = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const incomingState = location?.state || null;
    const { quota, setQuota, history, setHistory, countdownText, isLimitReached } = useAiQuota();

    const [inputPrompt, setInputPrompt] = useState("");
    const [selectedTone, setSelectedTone] = useState("Trendy");
    const [selectedImage, setSelectedImage] = useState(null);

    // Auto-prefill if navigated from post upload flow
    React.useEffect(() => {
        if (incomingState?.fileData) {
            const file = Array.isArray(incomingState.fileData) ? incomingState.fileData[0] : incomingState.fileData;
            if (file instanceof Blob || file instanceof File) {
                setSelectedImage(file);
                toast.info("Post image loaded for AI analysis!");
            }
        }
        if (incomingState?.caption) {
            setInputPrompt(incomingState.caption);
        }
    }, [incomingState]);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // Current displayed output
    const [currentCaption, setCurrentCaption] = useState(
        "\"Elegance isn't about being noticed, it's about being remembered. Designing experiences that breathe, speak, and last a lifetime.\""
    );
    const [currentHashtags, setCurrentHashtags] = useState([
        "#LuxuryDesign", "#Minimalism", "#CreativeStrategy", "#FameoX"
    ]);

    const fileInputRef = useRef(null);

    const handleGenerate = async (overridePrompt = null, overrideTone = null) => {
        const textToUse = overridePrompt !== null ? overridePrompt : inputPrompt;
        const toneToUse = overrideTone !== null ? overrideTone : selectedTone;

        if (!textToUse && !selectedImage) {
            toast.info("Please enter a prompt or attach an image.");
            return;
        }

        if (isLimitReached) {
            toast.warn(`Daily AI limit reached (15/15). Resets in ${countdownText}`);
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("prompt", textToUse);
            formData.append("tone", toneToUse);
            formData.append("type", "post");

            if (selectedImage) {
                const compressed = await compressImageForAi(selectedImage, 512);
                formData.append("image", compressed);
            }

            const { data } = await axios.post("/ask-fameo/generate", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (data?.status && data?.data) {
                const bestCaption = data.data.captions?.[0]?.text || "Caption generated successfully!";
                const tags = data.data.hashtags || [];

                setCurrentCaption(`"${bestCaption}"`);
                setCurrentHashtags(tags);

                if (data.quota) {
                    setQuota(data.quota);
                }

                // Add to local history list
                const newHistoryItem = {
                    prompt: textToUse || "Visual caption",
                    caption: bestCaption,
                    hashtags: tags,
                    createdAt: new Date(),
                };
                setHistory((prev) => [newHistoryItem, ...(prev || [])]);

                toast.success("✨ Caption generated by Fameo AI!");
            } else {
                toast.error(data?.message || "Failed to generate caption.");
            }
        } catch (err) {
            if (err.response?.status === 429) {
                const q = err.response.data.quota;
                if (q) setQuota(q);
                toast.error(err.response.data.message || "Daily AI limit reached!");
            } else {
                toast.error(err.response?.data?.message || "Failed to generate. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCardPrompt = (cardPrompt, tone) => {
        setInputPrompt(cardPrompt);
        setSelectedTone(tone);
        handleGenerate(cardPrompt, tone);
    };

    const handleCopy = () => {
        const fullContent = `${currentCaption.replace(/^"|"$/g, '')}\n\n${currentHashtags.join(" ")}`;
        navigator.clipboard.writeText(fullContent);
        setCopied(true);
        toast.success("Copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
    };

    const filteredHistory = (history || []).filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            item.prompt?.toLowerCase().includes(q) ||
            item.caption?.toLowerCase().includes(q) ||
            item.hashtags?.some((t) => t.toLowerCase().includes(q))
        );
    });

    return (
        <div className="ask-fameo-wrapper">
            <header className="ask-header">
                <div className="back-btn" onClick={() => navigate(-1)}>
                    <IoArrowBack />
                    <span>Fameo-X</span>
                </div>
                <h1>Ask Fameo-X</h1>
                <div className="right-placeholder">
                    {/* Live Quota Pill in Header */}
                    <div style={{
                        background: isLimitReached ? "rgba(239, 68, 68, 0.15)" : "rgba(166, 83, 157, 0.12)",
                        color: isLimitReached ? "#ef4444" : "#a6539d",
                        padding: "6px 14px",
                        borderRadius: "20px",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        {isLimitReached ? (
                            <span>Resets: {countdownText}</span>
                        ) : (
                            <span>Credits: {quota.remaining} / {quota.limit}</span>
                        )}
                    </div>
                </div>
            </header>

            <div className="main-content">
                <div className="left-section">
                    <div className="prompts-grid">
                        <div
                            className="prompt-card"
                            onClick={() => handleCardPrompt("Turn this idea into a viral Instagram caption", "Viral & Engaging")}
                        >
                            <div className="icon-box purple">
                                <GoLightBulb />
                            </div>
                            <div className="text-content">
                                <h3>Turn this idea into a caption</h3>
                                <p>Transform concepts into viral content</p>
                            </div>
                        </div>

                        <div
                            className="prompt-card"
                            onClick={() => handleCardPrompt("Generate high-converting caption with trending hashtags", "Trendy")}
                        >
                            <div className="icon-box blue">
                                <HiOutlineHashtag />
                            </div>
                            <div className="text-content">
                                <h3>Generate captions + hashtags</h3>
                                <p>The full engagement package</p>
                            </div>
                        </div>

                        <div
                            className="prompt-card"
                            onClick={() => handleCardPrompt("Refine and polish my draft into a punchy caption", "Minimalist")}
                        >
                            <div className="icon-box indigo">
                                <PiMagicWandFill />
                            </div>
                            <div className="text-content">
                                <h3>Refine my caption</h3>
                                <p>Polish and perfect existing drafts</p>
                            </div>
                        </div>

                        <div
                            className="prompt-card"
                            onClick={() => handleCardPrompt("Make this sound ultra-premium, sophisticated, and luxury", "Luxury & Premium")}
                        >
                            <div className="icon-box violet">
                                <IoDiamondOutline />
                            </div>
                            <div className="text-content">
                                <h3>Make this sound premium</h3>
                                <p>Elevate tone for luxury branding</p>
                            </div>
                        </div>
                    </div>

                    <div className="output-container">
                        <div className="premium-tag">
                            <FaRegCheckCircle />
                            <span>{loading ? "GENERATING WITH GEMINI..." : "PREMIUM OUTPUT"}</span>
                        </div>

                        <div className="output-text">
                            <p>{loading ? "Analyzing prompt and generating creative variations..." : currentCaption}</p>
                        </div>

                        <div className="output-hashtags">
                            {!loading && currentHashtags.map((tag, idx) => (
                                <span key={idx}>{tag.startsWith("#") ? tag : `#${tag}`}</span>
                            ))}
                        </div>

                        <div className="output-actions">
                            <button
                                className="regenerate-btn"
                                onClick={() => handleGenerate()}
                                disabled={loading || isLimitReached}
                            >
                                <IoReloadOutline />
                                {loading ? "Crafting..." : "Regenerate"}
                            </button>
                            <button className="share-btn" onClick={handleCopy} title="Copy to clipboard">
                                {copied ? <FaCheck color="#10b981" /> : <FaCopy />}
                            </button>

                            {incomingState && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const cleanCaption = currentCaption.replace(/^"|"$/g, '');
                                        const returnPath = incomingState.fileData !== undefined ? '/UploadPost' : -1;
                                        navigate(returnPath, {
                                            state: {
                                                ...incomingState,
                                                caption: cleanCaption,
                                                tag: currentHashtags.map(t => t.replace(/^#/, '')),
                                            }
                                        });
                                        toast.success("✨ Caption applied to post!");
                                    }}
                                    style={{
                                        background: "linear-gradient(135deg, #6366f1, #909aff)",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "2rem",
                                        padding: "0.8rem 1.6rem",
                                        fontSize: "1.4rem",
                                        fontWeight: "600",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.6rem"
                                    }}
                                >
                                    <FaCheck /> Apply to Post
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="right-section">
                    <h2>Content History</h2>
                    <div className="search-bar">
                        <CiSearch />
                        <input
                            type="text"
                            placeholder="Search captions or keywords..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="history-list">
                        {filteredHistory.length > 0 ? (
                            filteredHistory.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="history-item"
                                    onClick={() => {
                                        setCurrentCaption(`"${item.caption}"`);
                                        setCurrentHashtags(item.hashtags || []);
                                        toast.info("Loaded from history!");
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="item-header">
                                        <span className="tag generated">GENERATED</span>
                                        <span className="timestamp">
                                            {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "Recent"}
                                        </span>
                                    </div>
                                    <p className="item-preview">{item.caption}</p>
                                    <div className="item-hashtags">
                                        {(item.hashtags || []).slice(0, 3).map((tag, tIdx) => (
                                            <span key={tIdx}>{tag.startsWith("#") ? tag : `#${tag}`}</span>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: "center", color: "#8c8fa0", padding: "2rem" }}>
                                {history && history.length > 0 ? "No matches found" : "Your generated captions will appear here."}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <footer className="input-footer">
                {/* Image attachment chip preview */}
                {selectedImage && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: "#fff",
                        padding: "4px 12px",
                        borderRadius: "16px",
                        marginBottom: "6px",
                        width: "fit-content",
                        fontSize: "0.85rem",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                    }}>
                        <span>Attached Image: {selectedImage.name}</span>
                        <MdClose
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedImage(null)}
                        />
                    </div>
                )}

                <div className="input-container">
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            if (e.target.files?.[0]) {
                                setSelectedImage(e.target.files[0]);
                                toast.info("Image attached for AI analysis!");
                            }
                        }}
                    />
                    <CiCirclePlus
                        className="action-icon"
                        title="Attach image to analyze"
                        onClick={() => fileInputRef.current?.click()}
                    />
                    <input
                        type="text"
                        placeholder={
                            isLimitReached
                                ? `Daily limit reached. Resets in ${countdownText}`
                                : "Drop your raw thought or prompt here..."
                        }
                        value={inputPrompt}
                        onChange={(e) => setInputPrompt(e.target.value)}
                        disabled={loading || isLimitReached}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleGenerate();
                            }
                        }}
                    />
                    <div
                        className="send-btn"
                        onClick={() => handleGenerate()}
                        style={{
                            opacity: (loading || isLimitReached || (!inputPrompt && !selectedImage)) ? 0.5 : 1,
                            cursor: (loading || isLimitReached || (!inputPrompt && !selectedImage)) ? "not-allowed" : "pointer",
                        }}
                    >
                        <IoMdSend />
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default AskFameo;
